// src/components/TenderFormModal.js
import { useState, useEffect } from 'react';
import api from '../utils/api';
import { TENDER_STATUSES, TENDER_SECTORS, TENDER_SOURCES, PRIORITIES } from '../utils/constants';

const EMPTY = {
  title:'', reference_number:'', client_name:'', sector:'underground_mining',
  source:'manual_research', source_url:'', status:'identified', priority:'medium',
  estimated_value:'', currency:'CAD', location:'', province:'', country:'Canada',
  issue_date:'', closing_date:'', decision_date:'', notes:'', go_nogo_reason:''
};

export default function TenderFormModal({ onClose, onSaved, existing }) {
  const [form, setForm] = useState(existing ? { ...existing } : { ...EMPTY });
  const [clients, setClients] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    api.get('/clients').then(setClients).catch(() => {});
  }, []);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) { setError('Title is required'); return; }
    setSaving(true); setError('');
    try {
      let saved;
      if (existing) {
        saved = await api.put(`/tenders/${existing.id}`, form);
      } else {
        saved = await api.post('/tenders', form);
      }
      onSaved(saved);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth:680 }}>
        <div className="modal-header">
          <span className="modal-title">{existing ? 'Edit Tender' : 'New Tender'}</span>
          <button className="btn btn-ghost" onClick={onClose} style={{ padding:'0.3rem' }}>✕</button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="modal-body" style={{ display:'flex', flexDirection:'column', gap:'1rem' }}>
            {error && (
              <div style={{ background:'var(--red-dim)', border:'1px solid var(--red)', borderRadius:'var(--radius-sm)', padding:'0.75rem', fontSize:'0.85rem', color:'var(--red)' }}>
                {error}
              </div>
            )}

            <div className="form-group">
              <label className="form-label">Tender Title *</label>
              <input value={form.title} onChange={e => set('title', e.target.value)} placeholder="e.g. Shaft Rehabilitation Services – McArthur River" required />
            </div>

            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'1rem' }}>
              <div className="form-group">
                <label className="form-label">Reference Number</label>
                <input value={form.reference_number} onChange={e => set('reference_number', e.target.value)} placeholder="RFP-2025-001" />
              </div>
              <div className="form-group">
                <label className="form-label">Client</label>
                {clients.length > 0 ? (
                  <select value={form.client_id || ''} onChange={e => set('client_id', e.target.value || null)}>
                    <option value="">Type name below or select…</option>
                    {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                ) : (
                  <input value={form.client_name} onChange={e => set('client_name', e.target.value)} placeholder="Client name" />
                )}
              </div>
            </div>

            {clients.length > 0 && !form.client_id && (
              <div className="form-group">
                <label className="form-label">Client Name (if not in list)</label>
                <input value={form.client_name} onChange={e => set('client_name', e.target.value)} placeholder="Client name" />
              </div>
            )}

            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'1rem' }}>
              <div className="form-group">
                <label className="form-label">Status</label>
                <select value={form.status} onChange={e => set('status', e.target.value)}>
                  {TENDER_STATUSES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Priority</label>
                <select value={form.priority} onChange={e => set('priority', e.target.value)}>
                  {PRIORITIES.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
                </select>
              </div>
            </div>

            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'1rem' }}>
              <div className="form-group">
                <label className="form-label">Sector</label>
                <select value={form.sector} onChange={e => set('sector', e.target.value)}>
                  {TENDER_SECTORS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Source</label>
                <select value={form.source} onChange={e => set('source', e.target.value)}>
                  {TENDER_SOURCES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                </select>
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">Source URL</label>
              <input type="url" value={form.source_url} onChange={e => set('source_url', e.target.value)} placeholder="https://buyandsell.gc.ca/…" />
            </div>

            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'1rem' }}>
              <div className="form-group">
                <label className="form-label">Estimated Value (CAD)</label>
                <input type="number" min="0" value={form.estimated_value} onChange={e => set('estimated_value', e.target.value)} placeholder="0" />
              </div>
              <div className="form-group">
                <label className="form-label">Location / Province</label>
                <input value={form.province} onChange={e => set('province', e.target.value)} placeholder="Saskatchewan" />
              </div>
            </div>

            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:'1rem' }}>
              <div className="form-group">
                <label className="form-label">Issue Date</label>
                <input type="date" value={form.issue_date || ''} onChange={e => set('issue_date', e.target.value)} />
              </div>
              <div className="form-group">
                <label className="form-label">Closing Date</label>
                <input type="date" value={form.closing_date || ''} onChange={e => set('closing_date', e.target.value)} />
              </div>
              <div className="form-group">
                <label className="form-label">Decision Date</label>
                <input type="date" value={form.decision_date || ''} onChange={e => set('decision_date', e.target.value)} />
              </div>
            </div>

            {(form.status === 'no_go' || form.status === 'lost') && (
              <div className="form-group">
                <label className="form-label">Go/No-Go Reason</label>
                <textarea value={form.go_nogo_reason} onChange={e => set('go_nogo_reason', e.target.value)} rows={2} placeholder="Why was this decision made?" />
              </div>
            )}

            <div className="form-group">
              <label className="form-label">Notes</label>
              <textarea value={form.notes} onChange={e => set('notes', e.target.value)} rows={3} placeholder="Key contacts, scope summary, strategy notes…" />
            </div>
          </div>

          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? 'Saving…' : (existing ? 'Save Changes' : 'Create Tender')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
